// color-exempt-file — retokenisation Phase 6 ui-themes : dark:bg-[#030712]/dark:bg-[#1a1a1a] preservent le rendu dark historique (voir memoire ui-themes) ; gradient purple-to-blue = brand-mark decoratif fixe, non lie au theme
import React, { useEffect, useState } from "react";
import { SignInForm } from "@core/components/auth/SignInForm";
import { SignUpForm } from "@core/components/auth/SignUpForm";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@core/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { SEOHead } from "../components/SEOHead";
import { getBrandName } from "@core/services/supportProduct";
import { setAuthReturnTo, peekAuthReturnTo } from "@core/lib/authReturnTo";

export default function AdminLoginPage() {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const from = (location.state as any)?.from?.pathname || "/";
  // Mode admin uniquement si l'utilisateur a été redirigé depuis une route /admin*
  // (sinon on affiche un écran de connexion générique pour ne pas divulguer
  // l'existence du panel support à un visiteur qui arrive juste sur /login).
  const isAdminContext = from.startsWith("/admin");
  const [mode, setMode] = useState<"signin" | "signup">("signin");

  // Google OAuth lit `auth_return_to` pour construire redirectTo et peut
  // contenir un reliquat d'un flow précédent (ex: "/"). On force la valeur
  // à `from` (généralement "/admin") pour que /auth/callback renvoie ici.
  useEffect(() => {
    setAuthReturnTo(from);
    return () => {
      if (peekAuthReturnTo() === from) {
        localStorage.removeItem("auth_return_to");
      }
    };
  }, [from]);

  // Redirect once the auth state has actually propagated.
  // Navigating synchronously from SignInForm.onSuccess causes a race:
  // AdminGuard would render before AuthContext.user is populated and
  // bounce the user straight back to /login.
  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleSuccess = () => {
    // no-op: navigation is driven by the useEffect above once `user` is set.
  };

  return (
    <div className="min-h-screen-safe bg-background dark:bg-[#030712] flex flex-col items-center justify-center p-6">
      <SEOHead title={t("common:seo_title_login")} noIndex />
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-2">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">
            {isAdminContext
              ? "DooDates Admin"
              : mode === "signup"
                ? t("auth:sign_up")
                : t("auth:sign_in")}
          </h1>
          <p className="text-muted-foreground">
            {isAdminContext
              ? t("auth:admin_subtitle")
              : mode === "signup"
                ? t("auth:create_account_subtitle", { brand: getBrandName() })
                : t("auth:sign_in_subtitle", { brand: getBrandName() })}
          </p>
        </div>

        <div className="bg-card dark:bg-[#1a1a1a] border border-border rounded-2xl p-1 shadow-2xl">
          {mode === "signup" && !isAdminContext ? (
            <SignUpForm
              hideHeader
              onSuccess={handleSuccess}
              onSwitchToSignIn={() => setMode("signin")}
            />
          ) : (
            <SignInForm
              hideHeader
              onSuccess={handleSuccess}
              onSwitchToSignUp={
                isAdminContext ? undefined : () => setMode("signup")
              }
            />
          )}
        </div>

        <button
          onClick={() => navigate("/")}
          className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {t("common:back_home")}
        </button>
      </div>
    </div>
  );
}
